import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Slider } from "@workspace/ui/components/ui/slider";
import { getSearchPriceBounds, searchPriceRefinement } from "@/lib/search-price-controls";

export function SearchPriceRangeSlider({
  min, max, valueMin, valueMax, onChange, label, rentalType, formatPrice, currency,
}: {
  min?: number | null;
  max?: number | null;
  valueMin?: number;
  valueMax?: number;
  onChange: (min: number | undefined, max: number | undefined) => void;
  label: string;
  rentalType?: string;
  formatPrice: (amount: number) => string;
  currency: string | null;
}) {
  const { t } = useTranslation();
  const bounds = getSearchPriceBounds(min, max, rentalType);
  const clamp = (value: number) => Math.min(bounds.max, Math.max(bounds.min, value));
  const currentMin = clamp(valueMin ?? bounds.min);
  const currentMax = Math.max(currentMin, clamp(valueMax ?? bounds.max));
  const [draft, setDraft] = useState<number[]>([currentMin, currentMax]);
  const [touched, setTouched] = useState({ min: false, max: false });
  useEffect(() => {
    setDraft([currentMin, currentMax]);
    setTouched({ min: false, max: false });
  }, [currentMin, currentMax, valueMin, valueMax, bounds.min, bounds.max]);
  // URL ranges can exceed the display cap: retain and show the exact amounts
  // until a thumb is deliberately moved. Merely rendering never rewrites them.
  const shownMin = touched.min ? draft[0] : valueMin ?? bounds.min;
  const shownMax = touched.max ? draft[1] : valueMax ?? bounds.max;
  const unbounded = touched.max ? draft[1] >= bounds.max : valueMax === undefined;
  return (
    <div data-testid="search-price-control">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <p className="mt-1 text-xs text-muted-foreground">
        {currency
          ? t("search.price_controls.source_currency", "Filter amounts are in {{currency}} (listing currency).", { currency })
          : t("search.price_controls.mixed_currency", "Amounts use each listing’s currency. Prices across currencies are not comparable.")}
      </p>
      <div className="mt-4 px-1">
        <Slider
          min={bounds.min}
          max={bounds.max}
          step={10}
          minStepsBetweenThumbs={0}
          value={draft}
          onValueChange={values => {
            setTouched(previous => ({
              min: previous.min || values[0] !== currentMin,
              max: previous.max || values[1] !== currentMax,
            }));
            setDraft(values);
          }}
          onValueCommit={values => {
            const [lo, hi] = searchPriceRefinement(values, bounds.min, bounds.max);
            onChange(
              touched.min || values[0] !== currentMin ? lo : valueMin,
              touched.max || values[1] !== currentMax ? hi : valueMax,
            );
            setTouched({ min: false, max: false });
          }}
        />
        <div className="mt-3 flex items-center justify-between gap-2 text-sm text-foreground">
          <span>{formatPrice(shownMin)}</span>
          <span>{unbounded
            ? t("search.price_controls.and_above", "{{price}} and above", { price: formatPrice(shownMax) })
            : formatPrice(shownMax)}</span>
        </div>
      </div>
    </div>
  );
}